import axios from "axios";

export const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Feb-2026 — global double-submit prevention.
// Two layers:
// 1. `useSubmitGuard` pushes its key here while a submit is in flight; the
//    interceptor below auto-attaches it to every POST/PUT inside the guard.
// 2. For POST/PUT requests OUTSIDE any guard (per-row approve/reject buttons,
//    page-level Save, etc.) we auto-derive an Idempotency-Key from
//    (url + body + 2-second time bucket). Two identical clicks within 2s
//    collapse into one server-side write via the backend idempotency cache;
//    after 2s a legitimate re-submit is allowed. Different bodies / URLs
//    always get different keys so unrelated work never collides.
let _currentIdempotencyKey = null;
export function setCurrentIdempotencyKey(k) { _currentIdempotencyKey = k; }
export function clearCurrentIdempotencyKey(k) {
  if (_currentIdempotencyKey === k) _currentIdempotencyKey = null;
}

function _cheapHash(s) {
  // djb2 — 32-bit, fast, no crypto dependency. Good enough for de-dup keys.
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36);
}

function _fingerprintKey(config) {
  const url = config.url || "";
  const body = config.data == null
    ? ""
    : (typeof config.data === "string" ? config.data : JSON.stringify(config.data));
  const bucket = Math.floor(Date.now() / 2000);   // 2s window
  return `auto-${bucket}-${_cheapHash(url + "|" + body)}`;
}

api.interceptors.request.use((config) => {
  const method = (config.method || "").toLowerCase();
  if (method !== "post" && method !== "put") return config;
  config.headers = config.headers || {};
  if (!config.headers["Idempotency-Key"]) {
    config.headers["Idempotency-Key"] =
      _currentIdempotencyKey || _fingerprintKey(config);
  }
  return config;
});

// Feb-2026 (Wave B) — dispatch a browser-level "supplar:mutation" event
// after every successful POST/PUT/DELETE so listening components (e.g. the
// live Dashboard) can invalidate their caches and refetch immediately.
// The event.detail carries {method, url} so subscribers can filter by
// endpoint if they want; we broadcast for ALL mutations so downstream
// widgets don't have to enumerate every endpoint.
api.interceptors.response.use(
  (response) => {
    const method = (response.config?.method || "").toLowerCase();
    if (method === "post" || method === "put" || method === "delete") {
      try {
        window.dispatchEvent(new CustomEvent("supplar:mutation", {
          detail: { method, url: response.config?.url || "" },
        }));
      } catch (_) { /* no-op */ }
    }
    return response;
  },
  (error) => Promise.reject(error),
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
